import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, List, ListItem, ListItemText,
  Snackbar, Stack, Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { FilterChip, SearchInput } from '../shared/ListFilters';
import DataTable from '../shared/DataTable';

const STATUS_COLORS = { draft: 'default', trial: 'info', active: 'success', unpublished: 'warning', suspended: 'error' };
const STATUSES = ['trial', 'active', 'unpublished', 'suspended', 'draft'];

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString('fi-FI') : '';
}

/** Nettisivut across tenants: status, trial, last publish, bookings via the site, and staff actions. */
export default function WebsitesTab() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [publications, setPublications] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const {
    data, loading, page, pageSize, totalCount, totalPages, setPage, setPageSize, setSearchQuery, setFilters,
    sortField, sortDirection, handleSort, refetch,
  } = usePaginatedList('/api/vadmin/websites/', {
    initialFilters: {},
    defaultSortField: 'updated_at',
    defaultSortDirection: 'desc',
    defaultPageSize: 50,
  });

  useEffect(() => {
    setFilters(statusFilter ? { status: statusFilter } : {});
  }, [statusFilter, setFilters]);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const reload = useCallback(async () => {
    if (typeof refetch === 'function') await refetch();
  }, [refetch]);

  const openDetail = async (row) => {
    setSelected(row);
    try {
      const [pubs, jobs] = await Promise.all([
        axios.get(`/api/vadmin/websites/${row.id}/publications/`),
        axios.get(`/api/vadmin/websites/${row.id}/generation-log/`),
      ]);
      setPublications(pubs.data);
      setLog(jobs.data);
    } catch {
      setPublications([]);
      setLog([]);
    }
  };

  const act = async (fn, successKey) => {
    setBusy(true);
    try {
      await fn();
      notify(t(successKey));
      await reload();
      if (selected) {
        const { data: pubs } = await axios.get(`/api/vadmin/websites/${selected.id}/publications/`);
        setPublications(pubs);
      }
    } catch (err) {
      notify(err?.response?.data?.detail || t('vadmin.websitesTab.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async (row) => {
    try {
      const { data: preview } = await axios.get(`/api/vadmin/websites/${row.id}/preview/`);
      window.open(preview.app_url, '_blank', 'noopener');
    } catch (err) {
      notify(err?.response?.data?.detail || t('vadmin.websitesTab.no_content'), 'error');
    }
  };

  const columns = [
    { key: 'organization_name', label: t('vadmin.organization'), render: (r) => r.organization_name, maxWidth: 260 },
    {
      key: 'subdomain', label: t('vadmin.websitesTab.address'), sortable: true,
      render: (r) => (r.status === 'trial' || r.status === 'active'
        ? <Link href={r.public_url} target="_blank" rel="noopener">{r.hostname}</Link>
        : r.hostname),
    },
    { key: 'status', label: t('vadmin.status'), sortable: true, width: '1%', render: (r) => <Chip size="small" color={STATUS_COLORS[r.status] || 'default'} label={t(`website.status.${r.status}`)} /> },
    { key: 'plan_name', label: t('vadmin.plan'), width: '1%', render: (r) => `${r.plan_name}${r.website_included ? ` (${t('vadmin.websitesTab.included')})` : ''}` },
    { key: 'trial_ends_at', label: t('vadmin.websitesTab.trial_end'), sortable: true, width: '1%', render: (r) => `${formatDate(r.trial_ends_at)}${r.trial_confirmed_at ? ' ✓' : ''}` },
    { key: 'last_published_at', label: t('vadmin.websitesTab.last_publish'), sortable: true, width: '1%', render: (r) => formatDate(r.last_published_at) },
    { key: 'bookings_30d', label: t('vadmin.websitesTab.bookings_30d'), sortable: true, align: 'right', width: '1%', render: (r) => r.bookings_30d },
  ];

  return (
    <Box data-testid="vadmin-websites-tab">
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <SearchInput value={search} onChange={(value) => { setSearch(value); setSearchQuery(value); }} placeholder={t('vadmin.websitesTab.search')} />
        {STATUSES.map((s) => (
          <FilterChip key={s} label={t(`website.status.${s}`)} active={statusFilter === s} onClick={() => setStatusFilter(statusFilter === s ? '' : s)} />
        ))}
      </Stack>
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        onRowClick={openDetail}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyIcon={<LanguageIcon />}
        emptyTitle={t('vadmin.websitesTab.empty')}
        emptyDescription={t('vadmin.websitesTab.empty_help')}
        pagination={{ page, totalPages, totalCount, pageSize, onPageChange: setPage, onPageSizeChange: setPageSize }}
      />

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        {selected && (
          <>
            <DialogTitle>{selected.organization_name}: {selected.hostname}</DialogTitle>
            <DialogContent dividers>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" onClick={() => openPreview(selected)} disabled={busy}>{t('vadmin.websitesTab.open_preview')}</Button>
                <Button size="small" onClick={() => act(() => axios.post(`/api/vadmin/websites/${selected.id}/force-republish/`), 'vadmin.websitesTab.republished')} disabled={busy}>{t('vadmin.websitesTab.force_republish')}</Button>
                <Button size="small" color="warning" onClick={() => act(() => axios.post(`/api/vadmin/websites/${selected.id}/unpublish/`), 'vadmin.websitesTab.unpublished')} disabled={busy}>{t('website.unpublish')}</Button>
                {selected.status !== 'suspended' ? (
                  <Button size="small" color="error" onClick={() => act(() => axios.post(`/api/vadmin/websites/${selected.id}/suspend/`), 'vadmin.websitesTab.suspended')} disabled={busy}>{t('vadmin.websitesTab.suspend')}</Button>
                ) : (
                  <Button size="small" onClick={() => act(() => axios.post(`/api/vadmin/websites/${selected.id}/unsuspend/`), 'vadmin.websitesTab.unsuspended')} disabled={busy}>{t('vadmin.websitesTab.unsuspend')}</Button>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('vadmin.websitesTab.detail_line', {
                  status: t(`website.status.${selected.status}`),
                  trial: formatDate(selected.trial_ends_at) || '-',
                  confirmed: selected.trial_confirmed_at ? formatDate(selected.trial_confirmed_at) : '-',
                  item: selected.stripe_addon_item_id || '-',
                  versions: selected.content_versions,
                })}
              </Typography>

              <Typography variant="subtitle2">{t('website.versions')}</Typography>
              <List dense>
                {publications.map((p) => (
                  <ListItem
                    key={p.id}
                    secondaryAction={!p.is_current ? (
                      <Button size="small" onClick={() => act(() => axios.post(`/api/vadmin/websites/${selected.id}/restore/`, { publication_id: p.id }), 'website.restored')} disabled={busy}>{t('website.restore')}</Button>
                    ) : <Chip size="small" color="success" label={t('website.current_version')} />}
                  >
                    <ListItemText primary={`${new Date(p.published_at).toLocaleString('fi-FI')} (v${p.content_version})`} secondary={t(`website.trigger.${p.trigger}`, { defaultValue: p.trigger })} />
                  </ListItem>
                ))}
                {publications.length === 0 && <ListItem><ListItemText primary={t('website.no_versions')} /></ListItem>}
              </List>

              <Typography variant="subtitle2" sx={{ mt: 2 }}>{t('vadmin.websitesTab.generation_log')}</Typography>
              <List dense>
                {log.map((job) => (
                  <ListItem key={job.id} alignItems="flex-start">
                    <ListItemText
                      primary={`${new Date(job.created_at).toLocaleString('fi-FI')}: L${job.level} ${job.status} ${job.model_name || ''} ${job.prompt_version || ''} ${job.field_path || ''}`}
                      secondary={(
                        <Box component="span" sx={{ display: 'block' }}>
                          {job.error && <Box component="span" sx={{ color: 'error.main', display: 'block' }}>{job.error}</Box>}
                          {job.raw_output?.guard?.removed?.length > 0 && (
                            <Box component="span" sx={{ display: 'block' }}>
                              {t('vadmin.websitesTab.guard_removed', { count: job.raw_output.guard.removed.length })}: {job.raw_output.guard.removed.map((r) => r.reason).join('; ')}
                            </Box>
                          )}
                          <Box component="pre" sx={{ m: 0, mt: 0.5, fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                            {JSON.stringify({ copy: job.raw_output?.copy, facts: job.fact_sheet_snapshot }, null, 1)}
                          </Box>
                        </Box>
                      )}
                    />
                  </ListItem>
                ))}
                {log.length === 0 && <ListItem><ListItemText primary={t('vadmin.websitesTab.no_log')} /></ListItem>}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>{t('website.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
